/**
 * The nomination counts on the landing pages ("My Overview").
 *
 * All read the same `ListNominations` result and all mean "awaiting a decision" — but for
 * different people:
 *
 *   BD        "My Nominations in Review"       what I submitted and am waiting on
 *   BD        "My Nominations to Confirm Attendance"  mine, approved, attendance not confirmed
 *   BD mgr    "Nominations Awaiting My Review" what my reports submitted that I may decide
 *   marketer  "Nominations Awaiting My Review" what I may decide
 *
 * The service narrows the list to what the caller may see (everything for a marketer or admin;
 * own plus their reports' for anyone else); these then narrow by who submitted it and who may
 * decide it.
 *
 * Here rather than in TaskOverview.tsx because the coverage config measures only `.ts`.
 */

import type { Nomination, NominationStatus } from '../client/EventCalendarClient';
import { mayDecideNomination, type EventHubRole } from './authorization';
import { canConfirmAttendance, isAttendanceConfirmed } from './attendance';

/** The one status that is still open. Named so the comparison is not a bare string. */
const AWAITING: NominationStatus = 'PENDING_REVIEW';

/**
 * Nominations awaiting a decision that this caller submitted themselves.
 *
 * A nomination with no status is not counted: counting an unknown state as pending would inflate
 * a number someone is meant to work through to zero.
 *
 * Compared case-insensitively and trimmed: `createdBy` is the principal the service recorded
 * and the alias comes from a JWT claim, and the two agreeing on case is not something this
 * code should depend on.
 *
 * An empty alias counts nothing rather than everything — an unauthenticated or claim-less
 * user has no submissions, and a blank-matches-blank comparison would otherwise count every
 * nomination whose creator the service did not record.
 */
export function countOwnAwaitingReview(
  nominations: readonly Nomination[],
  alias: string | undefined,
): number {
  const mine = alias?.trim().toLowerCase();
  if (!mine) return 0;
  return nominations.filter(
    (n) => n.status === AWAITING && n.createdBy?.trim().toLowerCase() === mine,
  ).length;
}

function sameLogin(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Opens the Nomination List on what {@link awaitsMyReview} counts. */
export const AWAITING_MY_REVIEW_HREF = '/event/nominations?status=PENDING_REVIEW&review=mine';

/** The Nomination List's `?review=` value for {@link AWAITING_MY_REVIEW_HREF}. */
export const REVIEW_MINE = 'mine';

/**
 * Whether a BD's list shows them someone else's nomination — which, since the service returns a
 * non-marketer only their own and their reports' (resolved through the manager chain), means
 * they manage someone who has nominated. The only manager signal the frontend has: a manager
 * whose reports have not nominated yet reads as not one, and has nothing to review anyway.
 */
export function seesReportsNominations(
  nominations: readonly Nomination[],
  alias: string | undefined,
): boolean {
  if (!alias?.trim()) return false;
  return nominations.some((n) => !!n.createdBy && !sameLogin(n.createdBy, alias));
}

/**
 * Whether `nomination` awaits this reader's decision: exactly the rows the list offers them
 * Approve / Reject on (mayDecideNomination). The same set the Nomination List's `?review=mine`
 * shows, so a count, its link and the buttons all agree.
 */
export function awaitsMyReview(
  nomination: Nomination,
  alias: string | undefined,
  roles: ReadonlySet<EventHubRole>,
): boolean {
  return mayDecideNomination(nomination, alias, roles);
}

/** How many nominations await this reader's decision — see {@link awaitsMyReview}. */
export function countAwaitingMyReview(
  nominations: readonly Nomination[],
  alias: string | undefined,
  roles: ReadonlySet<EventHubRole>,
): number {
  return nominations.filter((n) => awaitsMyReview(n, alias, roles)).length;
}

/** The Nomination List's `?attendance=` values. */
export const ATTENDANCE_CONFIRMED = 'confirmed';
export const ATTENDANCE_UNCONFIRMED = 'unconfirmed';

/**
 * Whether `nomination` waits on this BD to confirm the guest's attendance: one they may tick
 * (canConfirmAttendance — their own, and APPROVED) that is not ticked yet.
 */
export function awaitsAttendanceConfirmation(
  nomination: Nomination,
  alias: string | undefined,
): boolean {
  return canConfirmAttendance(nomination, alias) && !isAttendanceConfirmed(nomination);
}

/** How many nominations wait on this BD to confirm attendance — see {@link awaitsAttendanceConfirmation}. */
export function countToConfirmAttendance(
  nominations: readonly Nomination[],
  alias: string | undefined,
): number {
  return nominations.filter((n) => awaitsAttendanceConfirmation(n, alias)).length;
}

/**
 * Opens the Nomination List on exactly what {@link countToConfirmAttendance} counts — the BD's
 * own approved nominations not yet confirmed — where each row's Attendance Confirmed box can be
 * ticked straight away.
 */
export function toConfirmAttendanceHref(alias: string | undefined): string {
  const by = alias ? `&createdBy=${encodeURIComponent(alias)}` : '';
  return `/event/nominations?status=APPROVED${by}&attendance=${ATTENDANCE_UNCONFIRMED}`;
}
